import { isAuthorisedGtagUrl } from "../../../common/scriptSecurity";
import type { GoogleConsentState } from "../consent";
import { loadScript } from "./scriptLoader";

const configured = new Set<string>();
let bootstrapped = false;
let consentDefaultSent = false;

/** Ensures the global `gtag` shim exists before `gtag.js` has finished loading. */
function ensureGtag(): (...args: unknown[]) => void {
  window.dataLayer = window.dataLayer ?? [];

  if (!window.gtag) {
    // gtag.js reads its queue from the dataLayer as array-like argument lists,
    // so entries must preserve their positional shape.
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args as unknown as Record<string, unknown>);
    };
  }

  return window.gtag;
}

/**
 * Records the consent state Google tags must assume before any measurement
 * command runs.
 *
 * Only the first call has any effect: `default` is a page-level statement of the
 * pre-decision position, and Google ignores it once a state is established.
 * Later decisions go through {@link pushConsentUpdate}.
 */
export function pushConsentDefault(state: GoogleConsentState): void {
  if (consentDefaultSent) return;
  consentDefaultSent = true;
  ensureGtag()("consent", "default", state);
}

/**
 * Reports the visitor's decision, granting or withdrawing each signal.
 *
 * Sent after the `default` state, so tags loaded on this page adjust without a
 * reload; a withdrawal stops Google reading or writing storage from that point
 * on.
 */
export function pushConsentUpdate(state: GoogleConsentState): void {
  ensureGtag()("consent", "update", state);
}

/** Whether the page-level `default` consent state has been sent. */
export function isConsentDefaultSent(): boolean {
  return consentDefaultSent;
}

/**
 * Loads `gtag.js` and configures a GA4 property.
 *
 * The library is fetched once; further calls only add another `config`, which
 * is how a partner's property is measured alongside DesignMyNight's.
 *
 * @returns `true` once the property is configured and the library available.
 */
export async function configureGa4Property(measurementId: string): Promise<boolean> {
  const gtag = ensureGtag();

  const src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  if (!isAuthorisedGtagUrl(src)) return false;

  // Recorded before the library is waited on, so a decision withdrawn while it
  // is still loading still silences the property.
  const isNew = !configured.has(measurementId);
  configured.add(measurementId);

  let available = true;
  if (!bootstrapped) {
    bootstrapped = true;
    available = await loadScript(src);
    gtag("js", new Date());
  }

  if (isNew) gtag("config", measurementId, { send_page_view: false });

  return available;
}

/** Whether a property has been configured in this session. */
export function isPropertyConfigured(measurementId: string): boolean {
  return configured.has(measurementId);
}

/**
 * Sends a GA4 event to a single property.
 *
 * Resolves once Google acknowledges dispatch, or after `timeoutMs`, so callers
 * can hold a redirect open until conversion beacons have left the browser.
 */
export function sendGa4Event(
  measurementId: string,
  eventName: string,
  params: Record<string, unknown>,
  timeoutMs = 1000,
): Promise<void> {
  const gtag = window.gtag;
  if (!gtag) return Promise.resolve();

  return new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, timeoutMs);

    gtag("event", eventName, {
      ...params,
      send_to: measurementId,
      event_callback: () => {
        window.clearTimeout(timer);
        resolve();
      },
    });
  });
}

/**
 * Reads a field GA4 holds for a property, such as the identifiers that make a
 * session continuous across a domain boundary.
 *
 * `gtag('get', ...)` answers through a callback, which never runs when the
 * library was blocked, the property is not configured or the tag is still
 * loading. It is therefore time-boxed: the promise always settles, so a caller
 * holding a redirect open cannot be left waiting on Google.
 *
 * @returns the value, or `null` when it is unavailable within `timeoutMs`.
 */
export function getGa4Field(
  measurementId: string,
  field: "client_id" | "session_id",
  timeoutMs = 500,
): Promise<string | null> {
  const gtag = window.gtag;
  if (!gtag) return Promise.resolve(null);

  return new Promise<string | null>((resolve) => {
    let settled = false;
    const settle = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timer = window.setTimeout(() => settle(null), timeoutMs);

    try {
      gtag("get", measurementId, field, (value: unknown) => {
        window.clearTimeout(timer);
        settle(typeof value === "string" || typeof value === "number" ? String(value) : null);
      });
    } catch {
      window.clearTimeout(timer);
      settle(null);
    }
  });
}

/** Sends a GA4 `page_view` to a single property, mirroring the legacy partner tracker. */
export function sendGa4PageView(measurementId: string, pagePath: string): Promise<void> {
  return sendGa4Event(measurementId, "page_view", { page_path: pagePath, page_location: window.location.href });
}

/**
 * Stops GA4 from sending anything for every property configured so far, used
 * when consent is withdrawn after the library has already been injected.
 */
export function disableConfiguredGa4Properties(): void {
  setGa4PropertiesDisabled(true);
}

/** Re-enables every previously disabled property. */
export function enableConfiguredGa4Properties(): void {
  setGa4PropertiesDisabled(false);
}

function setGa4PropertiesDisabled(disabled: boolean): void {
  for (const measurementId of configured) {
    (window as unknown as Record<string, boolean>)[`ga-disable-${measurementId}`] = disabled;
  }
}

/** Clears configured properties. Test-only. */
export function resetGa4Properties(): void {
  configured.clear();
  bootstrapped = false;
  consentDefaultSent = false;
}
